import { z } from 'zod';
export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 40;
/**
 * Format rules for a store slug (the `{slug}` in `{slug}.mrigl.tn`). Reserved names and
 * impersonation checks need the database and are applied by the tenants module.
 *
 * - lower-case ASCII letters, digits and single hyphens
 * - starts and ends with a letter or digit
 * - not all digits, not an IDNA (`xn--`) label
 */
export function checkSlugFormat(slug) {
    if (slug.length < SLUG_MIN_LENGTH)
        return 'too_short';
    if (slug.length > SLUG_MAX_LENGTH)
        return 'too_long';
    if (!/^[a-z0-9-]+$/.test(slug))
        return 'invalid_characters';
    if (slug.startsWith('-') || slug.endsWith('-') || slug.includes('--'))
        return 'invalid_shape';
    if (/^[0-9]+$/.test(slug))
        return 'invalid_shape';
    return null;
}
/**
 * Comparison keys used for impersonation checks: hyphens removed and look-alike digits folded
 * to letters (0→o, 3→e, 4→a, 5→s, and 1→i as well as 1→l), so `b1at`, `bi-at` and `biat`
 * collide. Returns every distinct variant.
 */
export function slugComparisonKeys(slug) {
    const base = slug
        .replaceAll('-', '')
        .replaceAll('0', 'o')
        .replaceAll('3', 'e')
        .replaceAll('4', 'a')
        .replaceAll('5', 's');
    return [...new Set([base.replaceAll('1', 'i'), base.replaceAll('1', 'l')])];
}
/**
 * Labels that platform infrastructure uses or will use under the base domain. A store can
 * never take one. Brand-impersonation rules (banks, couriers, payment providers) are data
 * owned by the tenants module and reviewed by the product owner, not part of this list.
 */
export const PLATFORM_RESERVED_SLUGS = new Set([
    'www',
    'app',
    'admin',
    'api',
    'mail',
    'smtp',
    'imap',
    'pop',
    'ftp',
    'cdn',
    'static',
    'assets',
    'media',
    'status',
    'docs',
    'help',
    'support',
    'auth',
    'login',
    'dev',
    'staging',
    'test',
    'ns1',
    'ns2',
    'mx',
    'webmail',
    'autodiscover',
    'autoconfig',
    'internal',
    'metrics',
    'grafana',
    'mrigl',
]);
export function isPlatformReservedSlug(slug) {
    return PLATFORM_RESERVED_SLUGS.has(slug.toLowerCase());
}
export const slugSchema = z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .superRefine((value, ctx) => {
    const error = checkSlugFormat(value);
    if (error !== null) {
        ctx.addIssue({ code: 'custom', message: error });
    }
});
//# sourceMappingURL=slug.js.map